import { ThemeProvider } from "styled-components";
import { token } from "../../theme";
import {
  MSWindowContainer,
  MSWindowBarsContainer,
  MSWindowTopBarContainer,
  MSWindowTopBarButtonsContainer,
  MSWindowTopBarButton,
  MSWindowTopBarButtonMinimiseBar,
  MSWindowBottomBarContainer,
  UnderLineSpan,
  MSWindowContent,
  MSWindowTopBarImage,
  MSAddressBarContainer,
  MSAddressBar,
} from "./MSWindowsStyles";
import { useWindowManager } from "../../pages/HomePage/utils/WindowManager";
import { useDraggable } from "../../pages/HomePage/utils/useDraggable";
import { PositionType, WindowModel } from "../../pages/HomePage/utils/types";
import { Flex } from "antd";
import MSButton from "../MSButton/MSButton";
import { CiShare1 } from "react-icons/ci";
import { motion } from "motion/react";
import { useLayoutEffect } from "react";
// keep maximised windows clear of the taskbar
const TASKBAR_HEIGHT = 46;

interface MSWindowProps extends WindowModel {
  children: React.ReactNode;
  onCloseWindowClick: (id: string) => void;
  initialPosition?: PositionType;
}
const MSWindow = ({
  windowTitle,
  children,
  onCloseWindowClick,
  showBottomBar = false,
  zIndex,
  initialPosition,
  id,
  isMinimized,
  isMaximized = false,
  icon,
  showAddressBar = false,
  addressBarLink = "",
}: MSWindowProps) => {
  const {
    state,
    updateWindowPosition,
    bringToFront,
    toggleMinimise,
    toggleMaximise,
  } = useWindowManager();
  const isActive = state.activeWindowId === id;
  const { dragRef, position, setPosition, handleMouseDownForDrag } =
    useDraggable({
      onDrag: (newPosition) => {
        updateWindowPosition(id, newPosition);
      },
      initialPosition: initialPosition,
      disabled: isMaximized,
    });

  // Spawn positions assume a ~400px window; nudge bigger ones back on screen.
  // offsetWidth/Height ignore the scale() of the open animation.
  useLayoutEffect(() => {
    const el = dragRef.current;
    if (!el) return;
    const maxX = window.innerWidth - el.offsetWidth - 8;
    const maxY = window.innerHeight - TASKBAR_HEIGHT - el.offsetHeight - 8;
    const clamped = {
      x: Math.max(0, Math.min(position.x, maxX)),
      y: Math.max(0, Math.min(position.y, maxY)),
    };
    if (clamped.x !== position.x || clamped.y !== position.y) {
      setPosition(clamped);
      updateWindowPosition(id, clamped);
    }
    // only on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenNewTab = () => {
    if (addressBarLink) {
      window.open(addressBarLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <ThemeProvider theme={token}>
      <motion.div
        data-window
        ref={dragRef}
        style={{
          position: "absolute",
          left: isMaximized ? 0 : position.x,
          top: isMaximized ? 0 : position.y,
          width: isMaximized ? "100vw" : undefined,
          height: isMaximized ? `calc(100vh - ${TASKBAR_HEIGHT}px)` : undefined,
          zIndex,
          pointerEvents: isMinimized ? "none" : "auto",
        }}
        initial={{ opacity: 0, scale: 0.6, y: 40 }}
        animate={
          isMinimized
            ? {
                opacity: 0,
                scale: 0.2,
                // shrink down towards the taskbar
                y: window.innerHeight - (isMaximized ? 0 : position.y),
              }
            : { opacity: 1, scale: 1, y: 0 }
        }
        exit={{
          opacity: 0,
          scale: 0.85,
          transition: { duration: 0.16, ease: "easeIn" },
        }}
        transition={{ type: "spring", stiffness: 380, damping: 28, mass: 0.8 }}
      >
        <MSWindowContainer
          $maximized={isMaximized}
          onMouseDown={() => {
            bringToFront(id);
          }}
        >
          <MSWindowBarsContainer>
            <MSWindowTopBarContainer
              $active={isActive}
              onMouseDown={(e: React.MouseEvent) => {
                handleMouseDownForDrag(e);
              }}
              onDoubleClick={() => toggleMaximise(id)}
            >
              <Flex align="center" gap={4}>
                <MSWindowTopBarImage src={icon} />
                {windowTitle ? windowTitle : "Window"}
              </Flex>

              <MSWindowTopBarButtonsContainer>
                <MSWindowTopBarButton
                  onClick={() => {
                    //todo: minimise
                    toggleMinimise(id);
                  }}
                >
                  <MSWindowTopBarButtonMinimiseBar />
                </MSWindowTopBarButton>
                <MSWindowTopBarButton
                  aria-label={isMaximized ? "Restore" : "Maximize"}
                  onClick={() => toggleMaximise(id)}
                >
                  {isMaximized ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="9"
                      height="9"
                      viewBox="0 0 9 9"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2 0H9V6H7V9H0V3H2V0ZM3 1V3H7V5H8V1H3ZM1 4V8H6V4H1Z"
                        fill="black"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="9"
                      height="9"
                      viewBox="0 0 9 9"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9 0H0V9H9V0ZM8 2H1V8H8V2Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </MSWindowTopBarButton>
                <MSWindowTopBarButton
                  onClick={() => {
                    onCloseWindowClick(id);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="7"
                    viewBox="0 0 8 7"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0 0H2V1H3V2H5V1H6V0H8V1H7V2H6V3H5V4H6V5H7V6H8V7H6V6H5V5H3V6H2V7H0V6H1V5H2V4H3V3H2V2H1V1H0V0Z"
                      fill="black"
                    />
                  </svg>
                </MSWindowTopBarButton>
              </MSWindowTopBarButtonsContainer>
            </MSWindowTopBarContainer>
            <MSWindowBottomBarContainer showBottomBar={showBottomBar}>
              <UnderLineSpan>F</UnderLineSpan>ile{" "}
              <UnderLineSpan>E</UnderLineSpan>
              dit <UnderLineSpan>V</UnderLineSpan>iew{" "}
              <UnderLineSpan>H</UnderLineSpan>elp
            </MSWindowBottomBarContainer>
            {showAddressBar && (
              <MSAddressBarContainer>
                Address
                <MSAddressBar> {addressBarLink}</MSAddressBar>{" "}
                <MSButton
                  style={{ width: "20px", height: "20px", padding: 4 }}
                  onClick={handleOpenNewTab}
                >
                  {CiShare1({})}
                </MSButton>
              </MSAddressBarContainer>
            )}
          </MSWindowBarsContainer>
          <MSWindowContent
            $maximized={isMaximized}
            isWebsite={Boolean(addressBarLink && showAddressBar)}
          >
            {children}
          </MSWindowContent>
        </MSWindowContainer>
      </motion.div>
    </ThemeProvider>
  );
};

export default MSWindow;
